import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useLanguage from '../../Services/Language/useLanguageHook';
import trackingService, { TrackingEvent } from '../../Services/Tracking/TrackingService';
import LabelWithTooltip from '../Form/Inputs/LabelWithTooltip';
import styles from './BrandCard.module.scss';

export interface IBrandCardProps {
    brandingTitleString: string;
    title?: string;
    children: JSX.Element;
    navigateTo?: {
        pathName: string;
        search?: string;
    };
    tooltipMessage?: string;
    useNavigationCard?: boolean;
    testId?: string;
    textAlign?: 'left' | 'center' | 'right';
    track: TrackingEvent;
}

const BrandCard: React.FC<IBrandCardProps> = (props: IBrandCardProps) => {
    const [translations] = useLanguage();
    const navigate = useNavigate();

    const titleString = useMemo(() => {
        if (props.title) {
            return props.title;
        }
        return '';
    }, [translations, props.title]);

    const brandCardNavigation = () => {
        navigate({
            pathname: props.navigateTo!.pathName,
            search: props.navigateTo!.search,
        });
        trackingService.track({
            ...props.track,
            eventAction: `${props.track.eventAction}Navigated`,
            eventValue: `${props.navigateTo!.pathName}?${props.navigateTo!.search ?? ''}`,
        });
    };

    const brandingTitleStringWithTooltip = useMemo(() => {
        const brandingTitle = (
            <div className={props.useNavigationCard ? styles.brand_card_branding_simplified : styles.brand_card_branding}>{props.brandingTitleString}</div>
        );

        if (!props.tooltipMessage || props.useNavigationCard) {
            return brandingTitle;
        }

        return <LabelWithTooltip title={brandingTitle} tooltip={props.tooltipMessage} position="top" placement="right-end" />;
    }, [props.tooltipMessage, props.brandingTitleString, props.useNavigationCard]);

    const alignment = props.textAlign ?? 'center';
    let alignClass = styles.brand_card_align_center;
    if (alignment === 'left') {
        alignClass = styles.brand_card_align_left;
    } else if (alignment === 'right') {
        alignClass = styles.brand_card_align_right;
    }

    return (
        <div
            className={props.useNavigationCard ? styles.brand_card_simplified : styles.brand_card_full}
            onClick={props.useNavigationCard ? brandCardNavigation : undefined}
            onKeyPress={() => {}}
            role="button"
            tabIndex={props.useNavigationCard ? 0 : -1}
            data-testid={props.testId}
        >
            <div className={`${styles.brand_card_content} ${alignClass}`}>
                {brandingTitleStringWithTooltip}
                <div
                    className={props.useNavigationCard ? styles.brand_card_title_simplified : styles.brand_card_title}
                    data-testid={`${props.testId ?? 'BrandCard'}Title`}
                >
                    {titleString}
                </div>
                {props.children}
            </div>
        </div>
    );
};

export default BrandCard;
